// =============================================================================
// exports-client.http.ts — Impl RÉELLE (navigateur) de l'ExportsClient (S10-D3).
// =============================================================================
// EXPORT = γ SYNCHRONE (tranché GATE_0 S10) : chaque export est un TÉLÉCHARGEMENT DIRECT
// (GET → fichier CSV), PAS un job/async/polling — donc PAS un `fetch`, mais une navigation
// same-origin déclenchée par une ancre invisible. Le cookie httpOnly de session admin part
// automatiquement avec la navigation ; les 4 endpoints sont `AdminSessionGuard` + `@Header`
// CSV. GET → aucun CSRF (double-submit réservé aux écritures).
//
// SÉAM D2a-LECT : cette impl EST le défaut réel du DI seam (ExportsClientProvider). Nommée
// `HttpExportsClient` pour que le câblage soit reconnu comme « défaute sur le réel » (elle
// atteint l'API par navigation directe, l'équivalent HTTP d'un download). Le double
// déterministe (StubExportsClient) reste dans `.stub.ts`, injecté par les tests.
//
// SÉCURITÉ (RULE_REF_006) : aucun bearer/tenant fabriqué ni stocké ; la session est portée
// par le COOKIE httpOnly. Aucune donnée métier en localStorage.

import {
  EXPORT_PATHS,
  campaignRecipientsExportPath,
  cguSignaturesExportPath,
  complianceAuditExportPath,
  type CguSignaturesExportFilter,
  type ComplianceAuditExportFilter,
  type ExportKind,
  type ExportsClient,
} from '@/api/exports-client';

/**
 * Impl navigateur réelle (prod) : déclenche le téléchargement direct du CSV via une ancre
 * invisible (GET synchrone, aucun job/polling — GATE_0 S10). Pas d'I/O fetch : le navigateur
 * gère le streaming du fichier et l'attachement (Content-Disposition posé par l'API). NON
 * utilisée en test (jsdom) — le double `StubExportsClient` prend le relais via le provider.
 */
export class HttpExportsClient implements ExportsClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public download(kind: ExportKind): void {
    this.triggerDownload(EXPORT_PATHS[kind]);
  }

  public downloadCampaignRecipients(campaignId: string): void {
    this.triggerDownload(campaignRecipientsExportPath(campaignId));
  }

  public downloadCguSignatures(filter: CguSignaturesExportFilter): void {
    this.triggerDownload(cguSignaturesExportPath(filter));
  }

  public downloadComplianceAudit(filter: ComplianceAuditExportFilter): void {
    this.triggerDownload(complianceAuditExportPath(filter));
  }

  /** Déclenche une navigation-téléchargement same-origin (GET) vers le chemin d'export. */
  private triggerDownload(path: string): void {
    const url = `${this.baseUrl}${path}`;
    const anchor = document.createElement('a');
    anchor.href = url;
    // Laisse l'API nommer le fichier (Content-Disposition) ; same-origin → cookie de session.
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
}
